import React from 'react';
import { Vehicle } from 'models/Vehicle';
import {
  AnnualForecastKilometers,
  CompareDateCell,
  CompareValuesCell,
  ShowVehicleCell,
  TotalKilometers,
  VehicleTableConfig,
} from '../../../components/VehicleTableCells/VehicleTableCells.component';
import SyncDatVehicle from '../../../components/SyncDatVehicle';
import { VehicleImageCell } from 'router/subrouters/Dashboard/pages/Fleets/components/TableCellContent/TableCellContent.component';

type TableCols = Array<
  | keyof Pick<
      Vehicle,
      | 'images'
      | 'model'
      | 'vin'
      | 'licensePlate'
      | 'brand'
      | 'engineType'
      | 'pollutionClass'
      | 'avgFuelConsumption'
      | 'avgFuelConsumptionWltp'
      | 'vehicleWeight'
      | 'totalKm'
      | 'currentSpeed'
      | 'averageSpeed'
      | 'engineHours'
      | 'parkingHours'
      | 'lastServiceDate'
      | 'nextServicePlanned'
      | 'annualAgreedKilometers'
      | 'driverInfo'
      | 'annualForecastKilometers'
      | 'annualAgreedKilometers'
      | 'calculatedValueAtDate'
      | 'actualValueAtDate'
    >
  | 'datSyncCol'
  | 'showVehicle'
>;

export const columns: TableCols = [
  'images',
  'showVehicle',
  'vin',
  'datSyncCol',
  'licensePlate',
  'brand',
  'model',
  'engineType',
  'avgFuelConsumption',
  'avgFuelConsumptionWltp',
  'annualForecastKilometers',
  'annualAgreedKilometers',
  'totalKm',
  'calculatedValueAtDate',
  'actualValueAtDate',
  'currentSpeed',
  'averageSpeed',
  'vehicleWeight',
  'pollutionClass',
  'engineHours',
  'parkingHours',
  'lastServiceDate',
  'nextServicePlanned',
  'driverInfo',
];

export const headingTranslations: Record<TableCols[number], string> = {
  images: ' ',
  showVehicle: ' ',
  model: 'modelName',
  vin: 'vin',
  licensePlate: 'licensePlate',
  brand: 'brand',
  engineType: 'engineType',
  pollutionClass: 'pollutionClass',
  avgFuelConsumption: 'avgFuelConsumption',
  avgFuelConsumptionWltp: 'avgFuelConsumptionWltp',
  vehicleWeight: 'vehicleWeight',
  totalKm: 'totalKm',
  currentSpeed: 'currentSpeed',
  averageSpeed: 'averageSpeed',
  engineHours: 'engineHours',
  parkingHours: 'parkingHours',
  lastServiceDate: 'lastServiceDate',
  nextServicePlanned: 'nextServicePlanned',
  annualAgreedKilometers: 'annualAgreedKilometers',
  driverInfo: 'driverInfo',
  datSyncCol: 'syncWithDat',
  annualForecastKilometers: 'annualForecastKilometers',
  calculatedValueAtDate: 'calculatedValueAtDate',
  actualValueAtDate: 'actualValueAtDate',
};

const centeredTableCell = (v: any, unit?: string) => (
  <div className="centered-cell">
    {v} {unit}
  </div>
);

export const contentTransformations: Record<
  TableCols[number],
  (val: any, config?: VehicleTableConfig) => React.ReactNode
> = {
  images: (v, conf) => <VehicleImageCell val={v} config={conf} />,

  showVehicle: (v, conf) => <ShowVehicleCell val={v} config={conf} />,
  vin: (v, conf) => v,
  model: (v) => (
    <div className="model-name-cell">
      <span>{v}</span>
    </div>
  ),
  licensePlate: (v) => v,
  brand: (v) => v,
  engineType: (v) => v,
  pollutionClass: (v) => v,
  engineHours: (v) => centeredTableCell(v),
  parkingHours: (v) => centeredTableCell(v),
  vehicleWeight: (v) => centeredTableCell(v, 'kg'),
  annualAgreedKilometers: (v) => centeredTableCell(v, 'km'),
  totalKm: (v, conf) => <TotalKilometers val={v} config={conf} />,
  annualForecastKilometers: (v, conf) => (
    <AnnualForecastKilometers val={v} config={conf} />
  ),
  lastServiceDate: (v) => centeredTableCell(new Date(v).toLocaleDateString()),
  nextServicePlanned: (v) => (
    <CompareDateCell className="total-km-cell" value={v} refVal={new Date()} />
  ),
  avgFuelConsumptionWltp: (v) => centeredTableCell(v, 'l/100km'),
  avgFuelConsumption: (v, conf) => (
    <CompareValuesCell
      className="total-km-cell"
      value={v}
      refVal={conf.instance.avgFuelConsumptionWltp}
      sufix="l/100km"
    />
  ),
  currentSpeed: (v) => centeredTableCell(v, 'km/h'),
  averageSpeed: (v) => centeredTableCell(v, 'km/h'),
  calculatedValueAtDate: (v) => centeredTableCell(Math.round(v), '€'),
  actualValueAtDate: (v) => centeredTableCell(Math.round(v), '€'),
  driverInfo: (v) => v?.driverName,
  datSyncCol: (_, config) =>
    !config.instance.isSyncedWithDat ? (
      <SyncDatVehicle
        className="sync-button"
        vehicleId={config.instance._id}
        onSuccess={(vehicle) =>
          config.updateTableData((old: Vehicle[]) => {
            return old.map((oldEl) =>
              oldEl._id === vehicle._id ? vehicle : oldEl,
            );
          })
        }
      />
    ) : (
      <div className="successfully-synced-dat">Synced successfully</div>
    ),
};
